import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getIntegrationClient } from 'reactApp/modules/integration/integration.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';

import { type QueryParams, type SettingsState, EViewMode } from './settings.types';

const getSettingsStore = (state: any): SettingsState => state.settings;

const getPrevViewMode = (state: SettingsState): EViewMode => getSettingsStore(state).prevViewMode;

const getClient = createSelector(
    (state: RootState) => getIntegrationClient(state),
    ({ isTutoria }) => isTutoria
);

const getViewByStorage = createSelector(
    (_, storage): EStorageType => storage,
    (_, _storage, isMediaFolder: boolean): boolean => Boolean(isMediaFolder),
    (_) => EnvironmentSelectors.isPhone(),
    (state, storage) => storage === EStorageType.integration && getClient(state),
    (state) => getSettingsStore(state).viewModePhoto,
    (state) => getSettingsStore(state).viewMode,
    (state) => getSettingsStore(state).viewModeQuotaCleaner,
    (state) => (EnvironmentSelectors.isPhone() ? getSettingsStore(state).viewModePhotoMobile : EViewMode.thumbs),
    (state) => getSettingsStore(state).viewModeAttaches,
    (
        storage: EStorageType,
        isMediaFolder: boolean,
        isPhone: boolean,
        isTutoria,
        viewModePhoto,
        viewMode,
        viewModeQuotaCleaner,
        viewModePhotoMobile,
        viewModeAttaches
    ) => {
        const { isGallery, isAttaches, isSharedIncoming, isTrashBin, isDocuments, isQuotaCleaner, isIntegration, isInlineIntegration } =
            getStorage(storage as EStorageType);

        if (isInlineIntegration) {
            return EViewMode.thumbs;
        }

        if (isIntegration) {
            return isTutoria ? EViewMode.thumbs : EViewMode.list;
        }

        if ((isGallery || isMediaFolder) && !isPhone) {
            return viewModePhoto;
        }

        if (isGallery && isPhone) {
            return viewModePhotoMobile;
        }

        if (isAttaches) {
            return viewModeAttaches;
        }

        if (isSharedIncoming || isTrashBin) {
            return EViewMode.list;
        }

        if (isDocuments) {
            return EViewMode.thumbs;
        }

        if (isQuotaCleaner) {
            return viewModeQuotaCleaner;
        }

        if (ENABLE_FULL_RESPONSIVE && viewMode !== EViewMode.list) {
            return EViewMode.list;
        }

        return viewMode;
    }
);

const getQueryParams = createSelector(getSettingsStore, (store: SettingsState): QueryParams => store.queryParams);

const isSidebarOpened = createSelector(getSettingsStore, (store: SettingsState): boolean => store.sidebarOpened);

const getIsViewerOnly = createSelector(getQueryParams, (params) => params.viewerOnly);

export const SettingsSelectors = {
    getViewByStorage,
    getQueryParams,
    getPrevViewMode,
    isSidebarOpened,
    getIsViewerOnly,
};
